import React, { useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Close,
  Description,
  Download,
  FolderOpen,
} from "@mui/icons-material";
import {
  useGetCustomsDocumentsQuery,
  useLazyGetCustomsDocumentQuery,
} from "@/services";
import { useSnackbar } from "@/hooks";
import {
  getDocumentTypeFullLabel,
  getAuditStatusColor,
  getAuditStatusLabel,
  getAuditStatusIcon,
} from "@/utils/customs-helpers";
import { tokens } from "@/tokens";

interface DocumentsDrawerProps {
  open: boolean;
  onClose: () => void;
  entrySummaryNumber: string;
  clusterId?: string;
  documentCount: number;
  latestAuditStatus?: ClusterAuditStatus;
  tenantId?: string;
}

const DocumentsDrawer: React.FC<DocumentsDrawerProps> = ({
  open,
  onClose,
  entrySummaryNumber,
  clusterId,
  documentCount,
  latestAuditStatus,
  tenantId,
}) => {
  const { show } = useSnackbar();
  const [getCustomsDocument] = useLazyGetCustomsDocumentQuery();
  const [downloadingIds, setDownloadingIds] = React.useState<Set<string>>(new Set());

  // Fetch documents for this cluster
  const { data: documentsData, isLoading } = useGetCustomsDocumentsQuery(
    {
      clusterId: clusterId,
      pageSize: 100,
      pageNo: 1,
      ...(tenantId && { tenantId }),
    },
    {
      skip: !open || !clusterId,
    }
  );

  const documents = useMemo(() => {
    return documentsData?.data || [];
  }, [documentsData]);

  const handleDownload = async (doc: CustomsDocument) => {
    setDownloadingIds((prev) => new Set(prev).add(doc._id));
    try {
      const result = await getCustomsDocument({
        docId: doc._id,
        tenantId,
      }).unwrap();
      if (result?.downloadUrl) {
        window.open(result.downloadUrl, "_blank");
      }
    } catch (error) {
      show("Download Failed", (error as Error).message, "error");
    }
    setDownloadingIds((prev) => {
      const next = new Set(prev);
      next.delete(doc._id);
      return next;
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 420 },
          maxWidth: "100vw",
          bgcolor: tokens.bg.surface,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          borderBottom: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.hover,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: tokens.radius.md,
            bgcolor: "rgba(11,73,75,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <FolderOpen sx={{ color: tokens.brand.primary, fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.lg, color: tokens.text.primary, lineHeight: 1.3 }}>
              {entrySummaryNumber}
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted }}>
              {documentCount} document{documentCount !== 1 ? "s" : ""}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: tokens.text.muted }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Audit Status */}
      {latestAuditStatus && (
        <Box sx={{ px: 3, py: 1.5, borderBottom: `1px solid ${tokens.border.default}` }}>
          <Chip
            icon={getAuditStatusIcon(latestAuditStatus, 16) || undefined}
            label={getAuditStatusLabel(latestAuditStatus)}
            color={getAuditStatusColor(latestAuditStatus)}
            size="small"
            sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.xs, height: 24 }}
          />
        </Box>
      )}

      {/* Documents List */}
      <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress size={28} sx={{ color: tokens.brand.primary }} />
          </Box>
        ) : documents.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
              color: tokens.text.muted,
            }}
          >
            <Description sx={{ fontSize: 40, color: tokens.text.disabled, mb: 1 }} />
            <Typography sx={{ fontSize: tokens.fontSize.md }}>No documents found</Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ bgcolor: tokens.bg.surface, borderRadius: tokens.radius.lg, border: `1px solid ${tokens.border.default}`, overflow: "hidden" }}>
            {documents.map((doc, index) => (
              <React.Fragment key={doc._id}>
                <ListItem
                  sx={{
                    py: 1.5,
                    px: 2,
                    "&:hover": { bgcolor: tokens.bg.hover },
                  }}
                  secondaryAction={
                    <Tooltip title="Download">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDownload(doc)}
                        disabled={downloadingIds.has(doc._id)}
                        sx={{
                          color: tokens.brand.primary,
                          "&:hover": { bgcolor: "rgba(11,73,75,0.08)" },
                        }}
                      >
                        {downloadingIds.has(doc._id) ? (
                          <CircularProgress size={18} sx={{ color: tokens.brand.primary }} />
                        ) : (
                          <Download sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Description sx={{ color: tokens.text.muted, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Tooltip title={doc.filename} arrow>
                        <Typography
                          sx={{
                            fontWeight: tokens.fontWeight.semibold,
                            fontSize: tokens.fontSize.md,
                            color: tokens.text.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 220,
                          }}
                        >
                          {doc.filename}
                        </Typography>
                      </Tooltip>
                    }
                    secondary={
                      <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                        {getDocumentTypeFullLabel(doc.documentType)}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < documents.length - 1 && <Divider sx={{ borderColor: tokens.border.light }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${tokens.border.default}`, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md, px: 3, py: 0.75, color: tokens.text.body, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
        >
          Close
        </Button>
      </Box>
    </Drawer>
  );
};

export default DocumentsDrawer;
